import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DailyIframe from '@daily-co/daily-js';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AlertCircle,
  ArrowLeft,
  Brain,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Loader2,
  Mic,
  MicOff,
  RefreshCw,
  Sparkles,
  Stethoscope,
  UserRound,
  Video,
} from 'lucide-react';
import { useTelemedApi } from '../lib/telemed';
import { TelemedLiveDiagnostics, TelemedPatientBrief, TelemedTranscriptSegment, TelemedTranscriptSpeaker } from '../types/telemed';
import { useI18n } from '../i18n/LanguageContext';

type SpeechRecognitionEventResult = {
  isFinal: boolean;
  0: { transcript: string };
  length: number;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionEventResult>;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const TRANSCRIPT_EDIT_WINDOW_MS = 2 * 60 * 1000;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const maybeCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  return maybeCtor || null;
}

function getEditWindowRemainingSeconds(createdAtIso: string, nowMs: number) {
  const createdMs = new Date(createdAtIso).getTime();
  if (Number.isNaN(createdMs)) return 0;
  const remaining = Math.floor((createdMs + TRANSCRIPT_EDIT_WINDOW_MS - nowMs) / 1000);
  return Math.max(0, remaining);
}

function buildLocalSegment(text: string, speaker: 'CLINICIAN' | 'PATIENT' | 'UNSPECIFIED'): TelemedTranscriptSegment {
  return {
    id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text,
    speaker,
    createdAt: new Date().toISOString(),
  };
}

function formatSex(value: string | null | undefined) {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (!normalized) return 'Unknown sex';
  if (normalized === 'M' || normalized === 'MALE') return 'Male';
  if (normalized === 'F' || normalized === 'FEMALE') return 'Female';
  if (normalized === 'OTHER' || normalized === 'O') return 'Other';
  return value || 'Unknown sex';
}

function truncateText(value: string, maxLength: number) {
  const normalized = value.trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}...`;
}

export default function TelemedRoom() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const role = params.get('role') === 'guest' ? 'guest' : 'host';
  const { user } = useAuth0();
  const { tr, language } = useI18n();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const notesSaveTimerRef = useRef<number | null>(null);
  const diagnosticsTimerRef = useRef<number | null>(null);
  const lastSavedNotesRef = useRef('');
  const lastDiagnosticsSignatureRef = useRef('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);

  const [session, setSession] = useState<{
    sessionId: string;
    joinUrl: string;
    hostToken?: string | null;
    guestToken?: string | null;
    visitStatus?: string;
  } | null>(null);
  const [patientBrief, setPatientBrief] = useState<TelemedPatientBrief | null>(null);
  const [patientBriefLoading, setPatientBriefLoading] = useState(false);

  const [transcriptSegments, setTranscriptSegments] = useState<TelemedTranscriptSegment[]>([]);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptUpdatedAt, setTranscriptUpdatedAt] = useState<string | null>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const [editingSegmentText, setEditingSegmentText] = useState('');
  const [editingSegmentSaving, setEditingSegmentSaving] = useState(false);

  const [clinicianNotes, setClinicianNotes] = useState('');
  const [noteSaveState, setNoteSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [diagnostics, setDiagnostics] = useState<TelemedLiveDiagnostics | null>(null);
  const [diagnosticsUpdatedAt, setDiagnosticsUpdatedAt] = useState<string | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

  const [isTranscribing, setIsTranscribing] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [activeTranscriptSpeaker, setActiveTranscriptSpeaker] = useState<'CLINICIAN' | 'PATIENT'>(
    role === 'guest' ? 'PATIENT' : 'CLINICIAN',
  );
  const [autoDiagnostics, setAutoDiagnostics] = useState(true);
  const [endingVisit, setEndingVisit] = useState(false);
  const [visitEndedMessage, setVisitEndedMessage] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const activeTranscriptSpeakerRef = useRef<'CLINICIAN' | 'PATIENT'>(role === 'guest' ? 'PATIENT' : 'CLINICIAN');

  const speechRecognitionCtor = useMemo(() => getSpeechRecognitionCtor(), []);
  const sessionId = session?.sessionId ?? null;
  const clinicianDisplayName = useMemo(() => {
    const fullName = [user?.name, [user?.given_name, user?.family_name].filter(Boolean).join(' ')]
      .map((value) => String(value ?? '').trim())
      .find(Boolean);
    return fullName || tr('Current user');
  }, [tr, user?.family_name, user?.given_name, user?.name]);
  const patientDisplayName = useMemo(() => {
    const fullName = `${patientBrief?.firstName ?? ''} ${patientBrief?.lastName ?? ''}`.trim();
    return fullName || tr('Patient');
  }, [patientBrief?.firstName, patientBrief?.lastName, tr]);

  const getSpeakerMeta = useCallback(
    (speaker: TelemedTranscriptSpeaker) => {
      if (speaker === 'PATIENT') {
        return {
          roleLabel: tr('Patient'),
          displayName: patientDisplayName,
          badgeClass: 'border-rose-200 bg-rose-50 text-rose-700',
        };
      }
      return {
        roleLabel: tr('Clinician'),
        displayName: clinicianDisplayName,
        badgeClass: 'border-sky-200 bg-sky-50 text-sky-700',
      };
    },
    [clinicianDisplayName, patientDisplayName, tr],
  );

  const {
    createTelemedSession,
    updateVisitStatus,
    endTelemedSession,
    getLiveSessionState,
    getSessionPatientBrief,
    appendTranscriptChunk,
    updateTranscriptSegment,
    saveLiveNotes,
    runLiveDiagnostics,
  } = useTelemedApi();

  const loadPatientBrief = useCallback(
    async (targetSessionId: string) => {
      setPatientBriefLoading(true);
      try {
        const brief = await getSessionPatientBrief(targetSessionId);
        setPatientBrief(brief);
      } catch (err: any) {
        setLiveError(err?.message || tr('Failed to load patient briefing card.'));
      } finally {
        setPatientBriefLoading(false);
      }
    },
    [getSessionPatientBrief, tr],
  );

  const hydrateLiveState = useCallback(
    async (targetSessionId: string) => {
      try {
        const live = await getLiveSessionState(targetSessionId);
        setTranscriptText(live.transcriptText || '');
        setTranscriptSegments(live.transcriptSegments || []);
        setTranscriptUpdatedAt(live.transcriptUpdatedAt || null);
        setClinicianNotes(live.clinicianNotes || '');
        lastSavedNotesRef.current = live.clinicianNotes || '';
        setDiagnostics(live.liveDiagnostics || null);
        setDiagnosticsUpdatedAt(live.diagnosticsUpdatedAt || null);
      } catch (err: any) {
        setLiveError(err?.message || tr('Failed to load live telemedicine state.'));
      }
    },
    [getLiveSessionState, tr],
  );

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    activeTranscriptSpeakerRef.current = activeTranscriptSpeaker;
  }, [activeTranscriptSpeaker]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const s = await createTelemedSession(id);
        if (cancelled) return;
        setSession(s);
        if (s.sessionId) {
          await Promise.all([hydrateLiveState(s.sessionId), loadPatientBrief(s.sessionId)]);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || tr('Failed to create telemed session'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createTelemedSession, hydrateLiveState, id, loadPatientBrief, tr]);

  useEffect(() => {
    if (!session?.joinUrl || !containerRef.current) return;
    const token = role === 'host' ? session.hostToken : session.guestToken;
    const frame = DailyIframe.createFrame(containerRef.current, {
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: '0',
        borderRadius: '16px',
      },
    });

    frame
      .join({ url: session.joinUrl, token: token || undefined })
      .then(() => {
        if (id && role === 'host') {
          void updateVisitStatus(id, 'IN_SESSION');
        }
      })
      .catch((err) => {
        setError(err?.message || tr('Failed to join call'));
      });

    return () => {
      frame.leave().finally(() => frame.destroy());
    };
  }, [id, role, session, tr, updateVisitStatus]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (notesSaveTimerRef.current) window.clearTimeout(notesSaveTimerRef.current);
      if (diagnosticsTimerRef.current) window.clearTimeout(diagnosticsTimerRef.current);
    };
  }, []);

  const appendTranscript = useCallback(
    async (
      text: string,
      speaker: 'CLINICIAN' | 'PATIENT' | 'UNSPECIFIED' = role === 'guest' ? 'PATIENT' : 'CLINICIAN',
      replaceCurrent = false,
    ) => {
      if (!sessionId) return;
      const cleaned = text.trim();
      if (!cleaned) return;

      const localSegment = buildLocalSegment(cleaned, speaker);
      setTranscriptSegments((prev) => (replaceCurrent ? [localSegment] : [...prev, localSegment].slice(-500)));
      setTranscriptText((prev) => (replaceCurrent ? cleaned : [prev, cleaned].filter(Boolean).join('\n')));
      const nowIso = new Date().toISOString();
      setTranscriptUpdatedAt(nowIso);

      try {
        const updated = await appendTranscriptChunk(sessionId, { text: cleaned, speaker, replaceCurrent });
        setTranscriptText(updated.transcriptText || '');
        setTranscriptUpdatedAt(updated.transcriptUpdatedAt || nowIso);
        setTranscriptSegments(updated.transcriptSegments || []);
      } catch (err: any) {
        setLiveError(err?.message || tr('Failed to sync transcript chunk.'));
      }
    },
    [appendTranscriptChunk, role, sessionId, tr],
  );

  const saveEditedTranscriptSegment = useCallback(async () => {
    if (!sessionId || !editingSegmentId) return;
    const cleaned = editingSegmentText.trim();
    if (!cleaned) return;
    setEditingSegmentSaving(true);
    setLiveError(null);
    try {
      const updated = await updateTranscriptSegment(sessionId, editingSegmentId, cleaned);
      setTranscriptText(updated.transcriptText || '');
      setTranscriptUpdatedAt(updated.transcriptUpdatedAt || new Date().toISOString());
      setTranscriptSegments(updated.transcriptSegments || []);
      setEditingSegmentId(null);
      setEditingSegmentText('');
    } catch (err: any) {
      setLiveError(err?.message || tr('Unable to update transcript segment.'));
    } finally {
      setEditingSegmentSaving(false);
    }
  }, [editingSegmentId, editingSegmentText, sessionId, tr, updateTranscriptSegment]);

  const runDiagnosticsNow = useCallback(
    async (force = false) => {
      if (!sessionId) return;
      const hasEnoughInput = transcriptText.trim().length + clinicianNotes.trim().length > 30;
      if (!hasEnoughInput) return;
      setDiagnosticsLoading(true);
      setLiveError(null);
      try {
        const result = await runLiveDiagnostics(sessionId, { transcriptText, clinicianNotes, force, locale: language });
        setDiagnostics(result);
        setDiagnosticsUpdatedAt(result.generatedAt || new Date().toISOString());
        lastDiagnosticsSignatureRef.current = `${transcriptText}\n---\n${clinicianNotes}`;
      } catch (err: any) {
        setLiveError(err?.message || tr('Failed to run live diagnostics.'));
      } finally {
        setDiagnosticsLoading(false);
      }
    },
    [clinicianNotes, language, runLiveDiagnostics, sessionId, transcriptText, tr],
  );

  useEffect(() => {
    if (!sessionId) return;
    if (!autoDiagnostics) return;

    const signature = `${transcriptText}\n---\n${clinicianNotes}`;
    const textLength = transcriptText.trim().length + clinicianNotes.trim().length;
    if (textLength < 50) return;
    if (signature === lastDiagnosticsSignatureRef.current) return;

    if (diagnosticsTimerRef.current) window.clearTimeout(diagnosticsTimerRef.current);
    diagnosticsTimerRef.current = window.setTimeout(() => {
      void runDiagnosticsNow(false);
    }, 3500);

    return () => {
      if (diagnosticsTimerRef.current) window.clearTimeout(diagnosticsTimerRef.current);
    };
  }, [autoDiagnostics, clinicianNotes, runDiagnosticsNow, sessionId, transcriptText]);

  useEffect(() => {
    if (!sessionId) return;
    if (clinicianNotes === lastSavedNotesRef.current) return;

    setNoteSaveState('saving');
    if (notesSaveTimerRef.current) window.clearTimeout(notesSaveTimerRef.current);
    notesSaveTimerRef.current = window.setTimeout(() => {
      void saveLiveNotes(sessionId, clinicianNotes)
        .then(() => {
          lastSavedNotesRef.current = clinicianNotes;
          setNoteSaveState('saved');
        })
        .catch((err: any) => {
          setNoteSaveState('error');
          setLiveError(err?.message || tr('Failed to save clinical notes.'));
        });
    }, 700);

    return () => {
      if (notesSaveTimerRef.current) window.clearTimeout(notesSaveTimerRef.current);
    };
  }, [clinicianNotes, saveLiveNotes, sessionId, tr]);

  const startTranscription = useCallback(() => {
    if (!speechRecognitionCtor) {
      setTranscriptionError(tr('Speech recognition is not supported in this browser. Use Chrome or Edge.'));
      return;
    }

    try {
      setTranscriptionError(null);
      const recognition = new speechRecognitionCtor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';
      recognition.onresult = (event) => {
        let interim = '';
        const finalChunks: string[] = [];
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          const transcript = String(result?.[0]?.transcript || '').trim();
          if (!transcript) continue;
          if (result.isFinal) finalChunks.push(transcript);
          else interim += `${transcript} `;
        }
        setInterimText(interim.trim());
        if (finalChunks.length) {
          const chunk = finalChunks.join(' ').trim();
          setInterimText('');
          void appendTranscript(chunk, activeTranscriptSpeakerRef.current, false);
        }
      };
      recognition.onerror = (event) => {
        setTranscriptionError(event?.error ? `${tr('Transcription error')}: ${event.error}` : tr('Transcription error occurred.'));
        setIsTranscribing(false);
      };
      recognition.onend = () => setIsTranscribing(false);
      recognition.start();
      recognitionRef.current = recognition;
      setIsTranscribing(true);
    } catch (err: any) {
      setTranscriptionError(err?.message || tr('Unable to start transcription.'));
      setIsTranscribing(false);
    }
  }, [appendTranscript, speechRecognitionCtor, tr, language]);

  const stopTranscription = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setIsTranscribing(false);
    setInterimText('');
  }, []);

  const toggleTranscription = useCallback(() => {
    if (isTranscribing) stopTranscription();
    else startTranscription();
  }, [isTranscribing, startTranscription, stopTranscription]);

  const endVisit = useCallback(async () => {
    if (!id || role !== 'host') return;
    setEndingVisit(true);
    setLiveError(null);
    setVisitEndedMessage(null);
    stopTranscription();
    try {
      if (sessionId && clinicianNotes.trim() !== lastSavedNotesRef.current) {
        await saveLiveNotes(sessionId, clinicianNotes);
        lastSavedNotesRef.current = clinicianNotes;
      }
      await endTelemedSession(id);
      setVisitEndedMessage(tr('Visit ended. Telemedicine summary was added to the patient notes.'));
    } catch (err: any) {
      setLiveError(err?.message || tr('Failed to end telemedicine visit.'));
    } finally {
      setEndingVisit(false);
    }
  }, [clinicianNotes, endTelemedSession, id, role, saveLiveNotes, sessionId, stopTranscription, tr]);

  const formattedTranscriptTime = transcriptUpdatedAt
    ? new Date(transcriptUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const formattedDiagnosticsTime = diagnosticsUpdatedAt
    ? new Date(diagnosticsUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;
  const latestTranscriptSegment = transcriptSegments.length > 0 ? transcriptSegments[transcriptSegments.length - 1] : null;
  const collapsedSpeaker = getSpeakerMeta(latestTranscriptSegment?.speaker ?? activeTranscriptSpeaker);
  const collapsedTranscriptText = interimText || latestTranscriptSegment?.text || '';
  const activeSpeakerMeta = getSpeakerMeta(activeTranscriptSpeaker);

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4 px-6 py-6">
      <div className="flex shrink-0 items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/telemedicine')}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            <ArrowLeft size={16} /> {tr('Back')}
          </button>
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">{tr('Telemedicine')}</p>
            <h1 className="text-2xl font-semibold text-ink">{tr('Clinical Visit Room')}</h1>
            <p className="text-xs text-slate-500">{tr('Role')}: {role === 'host' ? tr('Provider (host)') : tr('Guest')}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={toggleTranscription}
            disabled={role !== 'host' || !speechRecognitionCtor}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${isTranscribing
              ? 'border-rose-200 bg-rose-50 text-rose-700'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {isTranscribing ? <MicOff size={15} /> : <Mic size={15} />}
            {isTranscribing ? tr('Stop Live Transcription') : tr('Start Live Transcription')}
          </button>

          <button
            onClick={() => void runDiagnosticsNow(true)}
            disabled={diagnosticsLoading || role !== 'host'}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {diagnosticsLoading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {tr('AI Clinical Analysis')}
          </button>

          <button
            onClick={() => void endVisit()}
            disabled={endingVisit || role !== 'host'}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
          >
            {endingVisit ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            {tr('End Visit & Save Summary')}
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm">
            <Video size={14} /> {session?.joinUrl || tr('Preparing call...')}
          </div>
        </div>
      </div>

      {(error || liveError || transcriptionError || visitEndedMessage) && (
        <div className="shrink-0 space-y-2">
          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {liveError && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <AlertCircle size={16} /> {liveError}
            </div>
          )}
          {transcriptionError && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <AlertCircle size={16} /> {transcriptionError}
            </div>
          )}
          {visitEndedMessage && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <Check size={16} /> {visitEndedMessage}
            </div>
          )}
        </div>
      )}

      <div className="grid flex-1 min-h-0 grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="xl:col-span-3 min-h-0">
          <div className="card flex h-full min-h-0 items-stretch p-4">
            <div ref={containerRef} className="flex h-full min-h-0 w-full items-center justify-center rounded-2xl bg-slate-100">
              {(loading || !session) && (
                <div className="flex flex-col items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="animate-spin" size={18} />
                  {tr('Connecting to Daily...')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="xl:col-span-1 min-h-0 overflow-y-auto pr-1 space-y-4">
          <div className="card p-3 border-indigo-100 bg-indigo-50/60">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <UserRound size={15} className="text-indigo-600" />
                <p className="text-sm font-semibold text-indigo-900">{tr('Patient Briefing')}</p>
              </div>
              {patientBriefLoading && <Loader2 size={14} className="animate-spin text-indigo-500" />}
            </div>

            {!patientBriefLoading && !patientBrief && (
              <p className="mt-2 text-xs text-indigo-700">{tr('Patient context unavailable.')}</p>
            )}

            {patientBrief && (
              <div className="mt-2 space-y-2 text-sm">
                <div className="rounded-lg border border-indigo-100 bg-white p-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900 truncate">
                      {patientBrief.firstName} {patientBrief.lastName}
                    </p>
                    <span className="text-[11px] text-slate-500">MRN {patientBrief.mrn}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {patientBrief.age !== null && patientBrief.age !== undefined ? `${patientBrief.age} ${tr('yrs')}` : tr('N/A')} • {tr(formatSex(patientBrief.sex))}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  {patientBrief.problems.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">{tr('Problems')}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {patientBrief.problems.slice(0, 4).map((problem) => (
                          <span key={problem} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                            {problem}
                          </span>
                        ))}
                        {patientBrief.problems.length > 4 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">
                            +{patientBrief.problems.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {patientBrief.allergies.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-rose-600">{tr('Allergies')}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {patientBrief.allergies.slice(0, 4).map((allergy) => (
                          <span key={allergy} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-100 text-rose-700">
                            {allergy}
                          </span>
                        ))}
                        {patientBrief.allergies.length > 4 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-700">
                            +{patientBrief.allergies.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {patientBrief.aiSummary && (
                  <div className="rounded-lg border border-indigo-100 bg-white p-2">
                    <div className="flex items-center gap-1 text-indigo-600 text-[11px] font-semibold uppercase tracking-wide">
                      <Sparkles size={12} /> {tr('AI Summary')}
                    </div>
                    <p className="mt-1 text-xs text-slate-700 leading-relaxed">
                      {truncateText(patientBrief.aiSummary.summaryText, 180)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card p-3 border-sky-100 bg-sky-50/50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-sky-900">{tr('Live Transcription')}</p>
                {isTranscriptExpanded && <p className="text-xs text-sky-700">{tr('Final transcript chunks are editable for 2 minutes.')}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-sky-700">
                  {formattedTranscriptTime ? `${tr('Updated')} ${formattedTranscriptTime}` : tr('No transcript yet')}
                </span>
                <button
                  onClick={() => setIsTranscriptExpanded((prev) => !prev)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sky-200 bg-white text-sky-700 hover:bg-sky-50"
                  aria-label={isTranscriptExpanded ? 'Collapse live transcription' : 'Expand live transcription'}
                >
                  {isTranscriptExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>
            </div>

            {role === 'host' && (
              <div className="mt-2 flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => setActiveTranscriptSpeaker('CLINICIAN')}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${activeTranscriptSpeaker === 'CLINICIAN'
                    ? 'border-sky-200 bg-sky-100 text-sky-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  <span className="uppercase tracking-wide">{tr('Clinician')}</span>
                  <span className="max-w-[110px] truncate">{clinicianDisplayName}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTranscriptSpeaker('PATIENT')}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${activeTranscriptSpeaker === 'PATIENT'
                    ? 'border-rose-200 bg-rose-100 text-rose-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  <span className="uppercase tracking-wide">{tr('Patient')}</span>
                  <span className="max-w-[110px] truncate">{patientDisplayName}</span>
                </button>
              </div>
            )}

            {!isTranscriptExpanded && (
              <button
                type="button"
                onClick={() => setIsTranscriptExpanded(true)}
                className="mt-2 w-full rounded-lg border border-sky-100 bg-white px-3 py-2 text-left"
              >
                {!collapsedTranscriptText && (
                  <p className="text-sm text-slate-500">{tr('Start live transcription to capture the consultation.')}</p>
                )}
                {collapsedTranscriptText && (
                  <div className="space-y-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${collapsedSpeaker.badgeClass}`}>
                        {collapsedSpeaker.roleLabel}
                      </span>
                      <span className="text-[11px] text-slate-500 truncate">{collapsedSpeaker.displayName}</span>
                    </div>
                    <p className={`text-sm leading-relaxed text-slate-700 ${interimText ? 'italic text-slate-500' : ''}`}>{collapsedTranscriptText}</p>
                  </div>
                )}
              </button>
            )}

            {isTranscriptExpanded && (
              <div className="mt-3 rounded-lg border border-sky-100 bg-white p-3 max-h-56 overflow-auto text-sm text-slate-700" dir="ltr">
                {transcriptSegments.length === 0 && !interimText && (
                  <p className="text-slate-500">{tr('Start live transcription to capture the consultation.')}</p>
                )}
                {transcriptSegments.map((segment) => {
                  const remainingSeconds = getEditWindowRemainingSeconds(segment.createdAt, nowMs);
                  const editable = role === 'host' && remainingSeconds > 0;
                  const isEditing = editingSegmentId === segment.id;
                  const speakerMeta = getSpeakerMeta(segment.speaker);
                  return (
                    <div key={segment.id} className="mb-3 last:mb-0 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex items-center gap-2">
                          <span className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${speakerMeta.badgeClass}`}>
                            {speakerMeta.roleLabel}
                          </span>
                          <span className="text-[11px] text-slate-500 truncate">{speakerMeta.displayName}</span>
                        </div>
                        {editable && !isEditing && (
                          <button
                            onClick={() => {
                              setEditingSegmentId(segment.id);
                              setEditingSegmentText(segment.text);
                            }}
                            className="text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                          >
                            {tr('Edit')} ({remainingSeconds}s)
                          </button>
                        )}
                      </div>

                      {!isEditing && <p className="leading-relaxed">{segment.text}</p>}
                      {isEditing && (
                        <div className="space-y-2">
                          <textarea
                            dir="ltr"
                            value={editingSegmentText}
                            onChange={(e) => setEditingSegmentText(e.target.value)}
                            className="w-full min-h-[70px] rounded border border-slate-200 px-2 py-1 text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => void saveEditedTranscriptSegment()}
                              disabled={editingSegmentSaving}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                            >
                              {editingSegmentSaving ? tr('Saving...') : tr('Save')}
                            </button>
                            <button
                              onClick={() => {
                                setEditingSegmentId(null);
                                setEditingSegmentText('');
                              }}
                              disabled={editingSegmentSaving}
                              className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-60"
                            >
                              {tr('Cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {interimText && (
                  <div className="flex min-w-0 items-start gap-2">
                    <span className={`mt-0.5 inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${activeSpeakerMeta.badgeClass}`}>
                      {activeSpeakerMeta.roleLabel}
                    </span>
                    <p className="italic text-slate-500">{interimText}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{tr('Clinician Notes')}</p>
                <p className="text-xs text-slate-500">{tr('Auto-saved and included in AI diagnostics.')}</p>
              </div>
              <span
                className={`text-xs ${noteSaveState === 'saving'
                  ? 'text-slate-500'
                  : noteSaveState === 'saved'
                    ? 'text-emerald-600'
                    : noteSaveState === 'error'
                      ? 'text-rose-600'
                      : 'text-slate-400'
                  }`}
              >
                {noteSaveState === 'saving'
                  ? tr('Saving...')
                  : noteSaveState === 'saved'
                    ? tr('Saved')
                    : noteSaveState === 'error'
                      ? tr('Save failed')
                      : tr('Idle')}
              </span>
            </div>
            <textarea
              dir="ltr"
              value={clinicianNotes}
              onChange={(e) => setClinicianNotes(e.target.value)}
              className="mt-3 w-full min-h-[120px] rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-100"
              placeholder={tr('Type live clinical observations, exam findings, and plan...')}
            />
          </div>

          <div className="card p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Brain size={16} className="text-indigo-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{tr('Live Diagnostics')}</p>
                  <p className="text-xs text-slate-500">{tr('AI analysis from transcript + notes.')}</p>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={autoDiagnostics}
                  onChange={(e) => setAutoDiagnostics(e.target.checked)}
                  className="rounded border-slate-300"
                />
                {tr('Auto-run')}
              </label>
            </div>

            {diagnosticsLoading && (
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={14} className="animate-spin" /> {tr('Running clinical analysis...')}
              </div>
            )}

            {!diagnostics && !diagnosticsLoading && (
              <p className="mt-3 text-sm text-slate-500">{tr('No diagnostics yet. Add transcript/notes then run analysis.')}</p>
            )}

            {diagnostics && (
              <div className="mt-3 space-y-3 text-sm">
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                  <p className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">{tr('Summary')}</p>
                  <p className="mt-1 text-slate-700">{diagnostics.summary}</p>
                  <p className="mt-2 text-[11px] text-indigo-600">
                    {tr('Confidence')} {(diagnostics.confidence * 100).toFixed(0)}%
                    {formattedDiagnosticsTime ? ` • ${tr('Updated')} ${formattedDiagnosticsTime}` : ''}
                  </p>
                </div>

                {diagnostics.redFlags.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-rose-600 font-semibold mb-1">{tr('Red Flags')}</p>
                    <div className="space-y-2">
                      {diagnostics.redFlags.map((flag, idx) => (
                        <div key={`${flag.title}-${idx}`} className="rounded-lg border border-rose-100 bg-rose-50 p-2">
                          <p className="font-medium text-rose-700">{tr(flag.title)}</p>
                          <p className="text-xs text-rose-700/90">{tr(flag.why)}</p>
                          <p className="text-xs text-slate-600 mt-1">{tr('Action')}: {tr(flag.action)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {diagnostics.differential.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1">{tr('Differential')}</p>
                    <div className="space-y-1">
                      {diagnostics.differential.map((item, idx) => (
                        <div key={`${item.label}-${idx}`} className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                          <p className="font-medium text-slate-800">
                            {tr(item.label)} <span className="text-xs text-slate-500">({tr(item.likelihood)})</span>
                          </p>
                          <p className="text-xs text-slate-600">{tr(item.rationale)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {diagnostics.missingQuestions.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1">{tr('Missing Questions')}</p>
                    <ul className="space-y-1 text-slate-700">
                      {diagnostics.missingQuestions.map((q, idx) => (
                        <li key={`${q}-${idx}`} className="flex items-start gap-2">
                          <Stethoscope size={13} className="mt-0.5 text-slate-400" />
                          <span>{tr(q)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {diagnostics.suggestedNextSteps.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1">{tr('Suggested Next Steps')}</p>
                    <ul className="space-y-1 text-slate-700">
                      {diagnostics.suggestedNextSteps.map((step, idx) => (
                        <li key={`${step}-${idx}`} className="flex items-start gap-2">
                          <ClipboardList size={13} className="mt-0.5 text-slate-400" />
                          <span>{tr(step)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
